import { useEffect, useRef, useState } from "react";
import { FormInput } from "../../Inputs/FormInput";
import { useUserLocale } from "@/hooks/useUserLocale";
import {
  formatCurrencyValue,
  padFractionOnBlur,
  parseLocaleNumber,
} from "@/lib/utils/currency";
import { useLiveGroupedInput } from "@/lib/utils/number-input-format";
import { MAX_DOUBLE_LENGTH } from "@/lib/utils/default-returner";

type TableCellContentLocaleNumberProps = {
  onFocusAction?: () => void;
  onBlurAction?: () => void;
  isFilter?: boolean;
  border?: string;
  editable: boolean;
  /** Canonical numeric string (dot decimal, e.g. "3519.5"). */
  value: string;
  /**
   * ISO 4217 code from the field definition. Set for CURRENCY fields (shows
   * symbol + currency minor units). Omit for plain DOUBLE → locale-grouped
   * decimal, no symbol; locale-aware input is identical either way.
   */
  currencyCode?: string;
  isSelected: boolean;
  isEditing: boolean;
  onChangeValue: (value: string) => void;
  error?: string | null;
  height?: string;
  surface?: string;
  fontSize?: string;
  padding?: string;
  rounded?: string;
  errorVisibility?: boolean;
  viewOnly?: boolean;
};

/**
 * Locale-aware numeric cell for DOUBLE and CURRENCY additional fields (replaces
 * the dot-only {@link TableCellContentDouble} for these types).
 *
 * STORAGE stays canonical (dot decimal, no grouping) so the value is a real
 * number for the backend + Excel/CSV exporters. The admin TYPES in their own
 * locale ("3.519,50") and SEES a formatted number ("3.519,50 €" for CURRENCY,
 * "3.519,5" for DOUBLE). On blur we parse the typed text with the user's locale
 * separators and emit the canonical number; an unparseable entry reverts.
 */
export const TableCellContentLocaleNumber: React.FC<
  TableCellContentLocaleNumberProps
> = ({
  isFilter,
  border = "outline-tertiary border focus-within:outline-focus",
  editable,
  value,
  currencyCode,
  isSelected,
  isEditing,
  onChangeValue,
  onFocusAction,
  onBlurAction,
  error,
  height = "h-9",
  surface = "",
  fontSize = "body-2-regular",
  padding = "",
  rounded = "rounded-lg",
  errorVisibility = false,
  viewOnly,
}) => {
  const locale = useUserLocale();
  const inputRef = useRef<HTMLInputElement>(null);
  // Live banking-style grouping while typing, caret preserved (decimal mode →
  // groups the integer part, leaves the fraction being typed alone).
  const formatLive = useLiveGroupedInput({ inputRef, locale, mode: "decimal" });
  // Pre-fill the editable draft with the value rendered in the user's LOCALE
  // form — GROUPED and padded to the right minimum decimals ("3.519,00") so a
  // freshly-focused field already reads tidily. NOT the raw canonical "12.345",
  // which a German user (and parseLocaleNumber) would misread as 12345 on blur.
  const editableOf = (v: string) =>
    v != null && v !== "" && Number.isFinite(Number(v))
      ? padFractionOnBlur(Number(v), locale, { currencyCode })
      : String(v ?? "");
  const [draft, setDraft] = useState(() => editableOf(value));

  useEffect(() => {
    setDraft(editableOf(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, locale]);

  const commit = () => {
    if (draft.trim() === "") {
      // Only clear if there actually was a value (don't churn an empty cell).
      if (value != null && value !== "") onChangeValue("");
    } else {
      const parsed = parseLocaleNumber(draft, locale);
      if (parsed !== null) {
        // No-op when the parsed value equals what's already stored, so a bare
        // focus→blur never rewrites the cell.
        if (!(value != null && value !== "" && Number(value) === parsed)) {
          onChangeValue(String(parsed));
        }
        setDraft(padFractionOnBlur(parsed, locale, { currencyCode }));
      } else {
        // Unparseable → revert to the last good stored value.
        setDraft(editableOf(value));
      }
    }
    if (onBlurAction) onBlurAction();
  };

  const display =
    value != null && value !== "" && Number.isFinite(Number(value))
      ? formatCurrencyValue(Number(value), currencyCode, locale)
      : "";

  return (
    <>
      {isEditing && isSelected && (editable || isFilter) ? (
        <div className="flex flex-col flex-1">
          <FormInput
            ref={inputRef}
            id={`edit`}
            border={border}
            rounded={rounded}
            margin=""
            disabled={viewOnly}
            iconPaddingLeft=""
            iconPaddingRight=""
            height={height}
            fontSize={fontSize}
            surface={surface}
            paddingInput={padding}
            value={draft}
            onChangeAction={(v) => {
              // Accept anything up to the length cap; real validation/parse
              // happens on blur so the admin can type "1.234,5" freely. Group
              // live as they type (caret preserved by the hook).
              if (v.length <= MAX_DOUBLE_LENGTH) setDraft(formatLive(v));
            }}
            onEnterAction={() => inputRef.current?.blur()}
            onBlurAction={commit}
            onFocusAction={onFocusAction}
            width="w-full"
          />
          {error && (
            <p
              className={`mt-1 body-3-regular ${
                errorVisibility ? "font-error" : "opacity-0"
              }`}
            >
              {error}
            </p>
          )}
        </div>
      ) : (
        <p>{display}</p>
      )}
    </>
  );
};
